'use client';

import {
    EvType,
    FullEnergyUsageInput,
    Occupancy,
} from '@/lib/actions/usage/constants';
import {
    addCleanHeatAndAcAtom,
    addHeatPumpWaterHeaterAtom,
    addHvacUseAtom,
    addInductionStoveAtom,
    addPeopleAtom,
    baseUsageAtom,
    electricHeatAtom,
    evsAtom,
    hasAcAtom,
    hasElectricStoveAtom,
    hasHeatPumpAtom,
    hhSizeAtom,
    sqftAtom,
    storiesAtom,
    utilityCoAtom,
    yearBuiltAtom,
    zipCodeAtom,
} from '@/ui/atoms/form-atoms';
import { safeParseInt } from '@choksheak/ts-utils/safeParseInt';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

export function useCustomerInputs() {
    const baseUsage = useAtomValue(baseUsageAtom);
    const zipCode = useAtomValue(zipCodeAtom);
    const yearBuilt = useAtomValue(yearBuiltAtom);
    const sqft = useAtomValue(sqftAtom);
    const hhSize = useAtomValue(hhSizeAtom);
    const utilityCo = useAtomValue(utilityCoAtom);

    const evs = useAtomValue(evsAtom);

    const addPeople = useAtomValue(addPeopleAtom);
    const addHvacUse = useAtomValue(addHvacUseAtom);

    const electricHeat = useAtomValue(electricHeatAtom);
    const hasHeatPump = useAtomValue(hasHeatPumpAtom);
    const hasAc = useAtomValue(hasAcAtom);
    const addCleanHeatAndAc = useAtomValue(addCleanHeatAndAcAtom);
    const addHeatpumpWaterHeater = useAtomValue(addHeatPumpWaterHeaterAtom);
    const addInductionStove = useAtomValue(addInductionStoveAtom);
    const stories = useAtomValue(storiesAtom);
    const hasElectricStove = useAtomValue(hasElectricStoveAtom);

    const inputDict = useMemo((): FullEnergyUsageInput => {
        return {
            input_usage: Math.floor(Number(baseUsage ?? 0)),
            sqft: Math.floor(Number(sqft ?? 0)),
            yr_built: Math.floor(Number(yearBuilt ?? 0)),
            hh_size: Math.floor(Number(hhSize ?? 0)),
            electric_heat: electricHeat,
            has_heatpump: hasHeatPump,
            has_electric_stove: hasElectricStove,
            zip: zipCode,
            occupancy: Occupancy.Occupied,
            has_ac: hasAc,
            add_ev: evs
                .filter((ev) => !!ev.type)
                .map((ev) => ({
                    ev_type: ev.type!,
                    miles: safeParseInt(ev.milesPerYear),
                    elec_pct: ev.type === EvType.phev ? 0.3 : 1.0,
                    home_chg_pct: safeParseInt(ev.homeChgPct, 0) / 100.0,
                })),
            add_people: Math.floor(Number(addPeople ?? 0)),
            add_hvac_use: addHvacUse ? 0.2 : 0,
            // TODO: Maybe add back in future.
            add_electrify: false,
            add_clean_heat_and_ac: addCleanHeatAndAc,
            add_heatpump_water_heater: addHeatpumpWaterHeater,
            add_induction_stove: addInductionStove,
            stories: safeParseInt(stories ?? '1'),
        };
    }, [
        addCleanHeatAndAc,
        addHeatpumpWaterHeater,
        addHvacUse,
        addInductionStove,
        addPeople,
        baseUsage,
        electricHeat,
        evs,
        hasAc,
        hasElectricStove,
        hasHeatPump,
        hhSize,
        sqft,
        stories,
        yearBuilt,
        zipCode,
    ]);

    // Check for data errors, not data completeness.
    const inputError = useMemo(() => {
        if (inputDict.zip.length !== 5) {
            return 'Zip code must be 5 digits.';
        }

        if (inputDict.sqft <= 0) {
            return 'Square footage must be greater than 0.';
        }

        if (inputDict.yr_built < 1900 || inputDict.yr_built > dayjs().year()) {
            return 'Year built must be between 1900 and 2025.';
        }

        if (inputDict.stories <= 0) {
            return 'Stories must be greater than 0.';
        }

        if (inputDict.hh_size + inputDict.add_people <= 0) {
            return 'At least 1 occupant is required.';
        }

        if (!inputDict.input_usage || inputDict.input_usage <= 0) {
            return 'Energy usage must be greater than 0.';
        }

        if (!utilityCo) {
            return 'You must select a utility company.';
        }

        return '';
    }, [
        inputDict.add_people,
        inputDict.hh_size,
        inputDict.input_usage,
        inputDict.sqft,
        inputDict.stories,
        inputDict.yr_built,
        inputDict.zip.length,
        utilityCo,
    ]);

    return { inputDict, inputError };
}
